import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, PointElementParams, PrimitiveElementDef } from '../../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator } from '../element.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../features/feature-style.model';

/**
 * Creates a standalone point primitive.
 *
 * Params define identity: x/y expressions, optional name.
 * Display defines styling: color, size, visible.
 *
 * Exposes computed values: { x, y }
 */
export class PointCreator implements ElementCreator<PrimitiveElementDef> {
  readonly types = ['point'];

  create(
    id: string,
    def: PrimitiveElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { elementFactory, featureTheme } = context;
    const params = def.params as PointElementParams;
    const objects: GeometryElement[] = [];

    const { valueContext } = context;
    if (!valueContext) {
      throw new Error(`PointCreator requires valueContext`);
    }

    const getX = valueContext.createThunk(params.x);
    const getY = valueContext.createThunk(params.y);

    // Resolve point style
    const pointStyle = resolveFeatureStyle('point', 'point', context, display, []);
    const visible = resolveFeatureVisibility('point', featureTheme, display, []);

    const pointResult = elementFactory.point({
      coords: [getX, getY],
      name: params.name ?? id,
      style: {
        ...pointStyle,
        visible,
        fixed: true,
      },
    });
    objects.push(pointResult.point);
    if (pointResult.label) {
      objects.push(pointResult.label);
    }

    return {
      id,
      type: 'point',
      objects,
      getComputedValues: () => ({
        x: getX,
        y: getY,
      }),
      destroy: () => objects.forEach((obj) => context.board.removeObject(obj)),
    };
  }
}
